"use client";

import { getImageUrl } from "@/lib/getImageUrl";
import { Box, Typography, useTheme } from "@mui/material";
import Button from "@mui/material/Button";
import { useTranslations } from "next-intl";
import Link from "next/link";
import MiningTable from "./MiningTable";

interface MiningHubContentProps {
  initialMiningData: FormattedCoin[];
}

interface FormattedCoin {
  rank: number;
  coin: string;
  dailyRevenue: number;
  marketCap: string;
  price: string;
  volume: string;
  powProduced: string;
  hashrate: string;
  algorithm: string;
}

// 通用平台卡片组件
function MiningPlatformCard({
  name,
  logo,
  url,
  description,
}: {
  name: string;
  logo: string;
  url: string;
  description: string;
}) {
  return (
    <Box
      onClick={() => window.open(url, "_blank")}
      sx={{
        p: "1rem",
        backgroundColor: "background.paper",
        borderRadius: "0.75rem",
        border: "1px solid",
        borderColor: "transparent",
        cursor: "pointer",
        transition: "all 0.2s ease-in-out",
        position: "relative",
        "&:hover": {
          borderColor: "primary.main",
        },
        display: "flex",
        alignItems: "center",
        gap: "1rem",
      }}>
      <Box
        sx={{
          flexShrink: 0,
          width: "3.5rem",
          height: "3.5rem",
          borderRadius: "50%",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}>
        <img src={logo} alt={name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      </Box>

      <Box>
        <Typography variant="h6" sx={{ fontSize: "var(--font-size-s)", fontWeight: 600, mb: "0.5rem" }}>
          {name}
        </Typography>
        <Typography variant="body2" sx={{ fontSize: "var(--font-size-xs)", color: "text.secondary" }}>
          {description}
        </Typography>
      </Box>
    </Box>
  );
}

export default function MiningHubContent({ initialMiningData }: MiningHubContentProps) {
  const t = useTranslations();
  const theme = useTheme();

  return (
    <Box
      sx={{
        mt: "-1rem",
        pt: "1rem",
        backgroundImage: `url(${getImageUrl("mininghub-bg.png", "dark")})`,
        backgroundSize: "1500px",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "top center",
        'html[data-theme="light"] &': {
          backgroundImage: `url(${getImageUrl("mininghub-bg.png", "light")})`,
        },
      }}>
      <Box
        sx={{
          maxWidth: "75rem",
          mx: "auto",
          pb: "18rem",
        }}>
        {/* Header Section */}
        <Box textAlign="center" sx={{ mt: "7rem", px: "1rem" }}>
          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontSize: "var(--font-size-3xl)",
              fontWeight: 600,
              lineHeight: 1.3,
              textAlign: "center",
              background: "linear-gradient(90deg, #FFFAF5 0%, #D9B179 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              'html[data-theme="light"] &': {
                background: "linear-gradient(90deg, #E7C5A1 0%, #A27D49 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              },
            }}>
            {t.rich("miningHub.header.title", { br: () => <br /> })}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              fontSize: "var(--font-size-l)",
              fontWeight: 400,
              lineHeight: 1.6,
              mt: "1.63rem",
            }}>
            {t("miningHub.header.subtitle")}
          </Typography>
        </Box>

        {/* Mining Table Section */}
        <Box textAlign="center" sx={{ mt: "12rem", px: "1rem" }}>
          <Typography
            variant="h3"
            component="h3"
            sx={{
              fontSize: "var(--font-size-3xl)",
              fontWeight: 600,
              lineHeight: 1.3,
              textAlign: "center",
            }}>
            {t("miningHub.table.title")}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: "text.secondary",
              mt: "0.5rem",
            }}>
            {t("miningHub.table.subtitle")}
          </Typography>
        </Box>
        {/* Mining Table */}
        <Box
          sx={{
            mt: "3rem",
            display: "flex",
            justifyContent: "center",
          }}>
          <MiningTable miningData={initialMiningData} isLoading={false} />
        </Box>

        {/* Mining Platform Directory */}
        <Box sx={{ mt: "6.8rem", px: "1rem" }}>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: "var(--font-size-3xl)",
              fontWeight: 600,
              textAlign: "center",
            }}>
            {t("miningHub.directory.title")}
          </Typography>

          {/* Mining Pools Section */}
          <Box sx={{ mt: "3rem" }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: "var(--font-size-s)",
                fontWeight: 600,
              }}>
              {t("miningHub.pools.title")}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
                gap: "1.5rem",
                mt: "1rem",
              }}>
              <MiningPlatformCard
                name="F2Pool"
                logo="/images/icon/miningLink/f2pool.png"
                url="https://www.f2pool.com"
                description={t("miningHub.pools.f2pool.desc")}
              />
              <MiningPlatformCard
                name="Antpool"
                logo="/images/icon/miningLink/antpool.png"
                url="https://www.antpool.com"
                description={t("miningHub.pools.antpool.desc")}
              />
              <MiningPlatformCard
                name="ViaBTC"
                logo="/images/icon/miningLink/viabtc.png"
                url="https://www.viabtc.com"
                description={t("miningHub.pools.viabtc.desc")}
              />
              <MiningPlatformCard
                name="Bitdeer"
                logo="/images/icon/miningLink/bitdeer.png"
                url="https://www.bitdeer.com"
                description={t("miningHub.pools.bitdeer.desc")}
              />
            </Box>
          </Box>

          {/* Mining Hardware Manufacturers Section */}
          <Box sx={{ mt: "4rem" }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: "var(--font-size-s)",
                fontWeight: 600,
              }}>
              {t("miningHub.hardware.title")}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
                gap: "1.5rem",
                mt: "1rem",
              }}>
              <MiningPlatformCard
                name="Bitmain"
                logo="/images/icon/miningLink/bitmain.png"
                url="https://www.bitmain.com"
                description={t("miningHub.hardware.bitmain.desc")}
              />
              <MiningPlatformCard
                name="MicroBT"
                logo="/images/icon/miningLink/microbt.png"
                url="https://www.microbt.com"
                description={t("miningHub.hardware.microbt.desc")}
              />
              <MiningPlatformCard
                name="Canaan"
                logo="/images/icon/miningLink/canaan.png"
                url="https://www.canaan.io"
                description={t("miningHub.hardware.canaan.desc")}
              />
            </Box>
          </Box>

          {/* DeFi Yield Farming Platforms Section */}
          <Box sx={{ mt: "4rem" }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: "var(--font-size-s)",
                fontWeight: 600,
              }}>
              {t("miningHub.defi.title")}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
                gap: "1.5rem",
                mt: "1rem",
              }}>
              <MiningPlatformCard
                name="Uniswap"
                logo="/images/icon/miningLink/uniswap.png"
                url="https://uniswap.org"
                description={t("miningHub.defi.uniswap.desc")}
              />
              <MiningPlatformCard
                name="Yearn Finance"
                logo="/images/icon/miningLink/yearn.png"
                url="https://yearn.finance"
                description={t("miningHub.defi.yearn.desc")}
              />
              <MiningPlatformCard
                name="Curve Finance"
                logo="/images/icon/miningLink/curve.png"
                url="https://curve.fi"
                description={t("miningHub.defi.curve.desc")}
              />
            </Box>
          </Box>
        </Box>

        {/* CTA Section */}
        <Box
          sx={{
            pt: "14rem",
            textAlign: "center",
            backgroundImage: `url(${getImageUrl("mininghub-divider.png", "dark")})`,
            backgroundSize: { xs: "130%", sm: "120%", lg: "85%" },
            backgroundRepeat: "no-repeat",
            backgroundPosition: "top 10rem center",
            'html[data-theme="light"] &': {
              backgroundImage: `url(${getImageUrl("mininghub-divider.png", "light")})`,
            },
          }}>
          <Typography
            variant="h5"
            sx={{
              color: "text.primary",
              fontWeight: 600,
              fontSize: "var(--font-size-xl)",
              mb: "2.5rem",
              lineHeight: 1.5,
            }}>
            {t.rich("miningHub.cta.title", {
              br: () => <br />,
              span: (chunks) => <span style={{ color: theme.palette.primary.main }}>{chunks}</span>,
            })}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center", mt: "3rem" }}>
            <Link href="/membership">
              <Button variant="contained" color="tertiary" size="large" sx={{ fontWeight: 600 }}>
                {t("miningHub.cta.button")}
              </Button>
            </Link>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
